import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import { supabase } from "../../supabaseClient";
import { getOrientedLayoutStyle } from "../../components/Tentvisuals";
import slotsConfigLegacy  from "../../components/SlotsConfig";
import slotsConfigSummer  from "../../components/SlotsConfig_Summer2026";
import useEventLayout from "../../components/Hooks/useEventLayout";
import "./SlotGrid.css";
import Pathway from "../../components/Pathway";
import "../../components/Pathway.css";

// ─── Constants ────────────────────────────────────────────────────────────────

const LEGACY_EVENT_KEY = ["xmas_2025", "easter_2026"];
const ZOOM_LEVELS      = [0.5, 0.65, 0.8, 1, 1.25, 1.5];
const DEFAULT_ZOOM     = 3;

const TENT_SUBDIVISIONS = {
  "17x17-pod":     { subs: ["A","B","C","D"], layout: "g22", label: "4-vendor pod (2×2)" },
  "20x20-double": { subs: ["A","B"],         layout: "g12", label: "Shared 20×20 (1×2)" },
  "20x20-single": { subs: ["A"],             layout: "g11", label: "Private 20×20" },
  "20x20":        { subs: ["A"],             layout: "g11", label: "20×20" },
  "10x10-single": { subs: ["A"],             layout: "g11", label: "10×10 Single" },
  "Food Court":   { subs: ["A"],             layout: "g11", label: "Food Court" },
  "sponsored":    { subs: ["A"],             layout: "g11", label: "Sponsored" },
  "space":        { subs: ["A"],             layout: "g11", label: "Space" },
  "space-3":      { subs: ["A","B","C"],     layout: "g31", label: "3-vendor space" },
  "reserved":     { subs: [],                layout: "g11", label: "Reserved" },
  "World-Fair-Trade": { subs:[], layout:"g11", label:"WFTO-zone"},
};

// Fixed key capitalization for reserved ("reserved" vs "Reserved")
const TENT_COLORS = {
  "17x17-pod":     "#ffaa6e",
  "20x20-double": "#74bfed",
  "20x20-single": "#bc8fe8",
  "20x20":        "#d9d9d9",
  "10x10-single": "#ff8080",
  "Food Court":   "#5ee8a8",
  "20x20_open":   "#64cff5",
  "sponsored":    "#ffe44d",
  "reserved":     "#f590b0", // Explicit pink for reserved
  "space":        "#feca62",
  "space-3":      "#f6b23e",
  "World-Fair-Trade":"#ffffff"
};

const TENT_LABELS = {
  "17x17-pod":     "17×17 Pod",
  "20x20-double": "20×20 Sharing",
  "20x20-single": "20×20 Individual",
  "10x10-single": "10×10 Single",
  "20x20":        "20×20",
  "reserved":     "Reserved",
  "Food Court":   "Food Court",
  "space":        "Space",
  "space-3":      "Space (3)",
  "World-Fair-Trade": "WFTO",
};

const FACING_LABEL = {
  right: "Opens → right",
  left:  "Opens ← left",
  top:   "Opens ↑ top",
  down:  "Opens ↓ bottom",
};

const SUBTLE = "2px solid rgba(0,0,0,0.12)";
const OPEN   = "3px solid #22c55e";

const CrownIcon = () => (
  <svg width="9" height="9" viewBox="0 0 12 12" fill="none" aria-label="Self-selected">
    <path d="M1 9h10M1.5 9L2.5 4l3 3 1-4 1 4 3-3 1 5" stroke="#f59e0b" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
  </svg>
);

const PencilIcon = () => (
  <svg width="9" height="9" viewBox="0 0 12 12" fill="none" aria-label="Admin assigned">
    <path d="M8 2l2 2-6 6H2V8l6-6z" stroke="#6366f1" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
  </svg>
);

const LockIcon = () => (
  <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-label="Reserved">
    <rect x="2.5" y="5" width="7" height="5.5" rx="1" stroke="#78716c" strokeWidth="1.3"/>
    <path d="M4 5V3.5a2 2 0 0 1 4 0V5" stroke="#78716c" strokeWidth="1.3" strokeLinecap="round"/>
  </svg>
);

const getFacingBorders = (facing) => ({
  borderTop:    facing === "top"   ? OPEN : SUBTLE,
  borderBottom: facing === "down"  ? OPEN : SUBTLE,
  borderLeft:   facing === "left"  ? OPEN : SUBTLE,
  borderRight:  facing === "right" ? OPEN : SUBTLE,
});

const getArrowStyle = (facing) => {
  const base = { position: "absolute", zIndex: 2, pointerEvents: "none" };
  switch (facing) {
    case "right": return { ...base, right: 3,  top: "50%",  transform: "translateY(-50%) rotate(0deg)" };
    case "left":  return { ...base, left: 3,   top: "50%",  transform: "translateY(-50%) rotate(180deg)" };
    case "top":   return { ...base, top: 3,    left: "50%", transform: "translateX(-50%) rotate(-90deg)" };
    case "down":  return { ...base, bottom: 3, left: "50%", transform: "translateX(-50%) rotate(90deg)" };
    default:      return base;
  }
};

const FacingArrow = ({ facing }) => (
  <svg style={getArrowStyle(facing)} width="9" height="9" viewBox="0 0 10 10" fill="none" aria-hidden>
    <path d="M2 5h6M5 2l3 3-3 3" stroke="#22c55e" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

// Hatch style for reserved slots on the map & subgrid
const reservedHatchStyle = (baseColor = "#f590b0") => ({
  backgroundImage: `repeating-linear-gradient(45deg, rgba(245, 144, 176, 0.85), rgba(245, 144, 176, 0.85) 6px, ${baseColor} 6px, ${baseColor} 12px)`,
});

const TileSubPreview = ({ slotId, subBookings, subReservations, effectiveType, isReserved }) => {
  const def = TENT_SUBDIVISIONS[effectiveType];
  if (!def || def.subs.length <= 1) return null;
  const baseColor = TENT_COLORS[effectiveType] || "#ccc";

  return (
    <div className={`sg-tile-subgrid sg-tile-subgrid--${def.layout}`}>
      {def.subs.map(sub => {
        const key      = `${slotId}:${sub}`;
        const entry    = subBookings[key];
        const isResSub = !entry && !!subReservations[key];
        return (
          <div
            key={sub}
            className={[
              "sg-tile-subcell",
              entry ? "sg-tile-subcell--filled" : isResSub ? "sg-tile-subcell--reserved" : "sg-tile-subcell--empty",
            ].join(" ")}
            style={isResSub ? reservedHatchStyle("#f590b0") : undefined}
            title={entry ? entry.vendorName : isResSub ? `${slotId}${sub} — reserved` : `${slotId}${sub} — empty`}
          >
            {entry
              ? <span className="sg-tile-subcell-init">{entry.vendorName?.[0]?.toUpperCase()}</span>
              : isResSub
                ? <span className="sg-tile-subcell-id" style={{ color: "#881337", fontWeight: "bold" }}>R</span>
                : <span className="sg-tile-subcell-id">{sub}</span>
            }
          </div>
        );
      })}
    </div>
  );
};

const ZoomControls = ({ zoomIndex, onZoomIn, onZoomOut, onReset }) => (
  <div className="sg-zoom-controls">
    <button className="sg-zoom-btn" onClick={onZoomOut} disabled={zoomIndex === 0} aria-label="Zoom out">
      <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
        <circle cx="6" cy="6" r="4.5" stroke="currentColor" strokeWidth="1.5"/>
        <path d="M10 10l2.5 2.5M4 6h4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
      </svg>
    </button>
    <button className="sg-zoom-btn sg-zoom-btn--label" onClick={onReset}>
      {Math.round(ZOOM_LEVELS[zoomIndex] * 100)}%
    </button>
    <button className="sg-zoom-btn" onClick={onZoomIn} disabled={zoomIndex === ZOOM_LEVELS.length - 1} aria-label="Zoom in">
      <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
        <circle cx="6" cy="6" r="4.5" stroke="currentColor" strokeWidth="1.5"/>
        <path d="M10 10l2.5 2.5M4 6h4M6 4v4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
      </svg>
    </button>
  </div>
);

const UndoToast = ({ toast, onUndo, onDismiss }) => {
  if (!toast) return null;
  return (
    <div className="sg-toast">
      <span>{toast.message}</span>
      <button className="sg-toast-undo" onClick={onUndo}>Undo</button>
      <button className="sg-toast-dismiss" onClick={onDismiss}>✕</button>
    </div>
  );
};

const SubSlotFloorPlan = ({
  slot, effectiveType, subBookings, subReservations,
  selectedSub, onSelectSub, onRemoveSub, onUnreserveSub, facing,
}) => {
  const def = TENT_SUBDIVISIONS[effectiveType];
  const orientedStyle = def ? getOrientedLayoutStyle(def.layout, facing) : undefined;

  if (!def || def.subs.length === 0) return null;

  const color  = TENT_COLORS[effectiveType] || "#ccc";
  const isMulti = def.subs.length > 1;

  return (
    <div className="sg-floorplan">
      <div className="sg-floorplan-label">
        {isMulti ? "Floor plan — click a sub-slot to assign or reserve" : "Slot"}
      </div>
      <div className={`sg-floorplan-grid sg-floorplan-grid--${def.layout}`} style={orientedStyle}>
        {def.subs.map(sub => {
          const booking = subBookings[sub];
          const isResSub = !booking && !!subReservations[sub];

          const isSelected = selectedSub === sub;
          const subLabel   = isMulti ? `${slot.label}${sub}` : slot.label;

          return (
            <div
              key={sub}
              className={[
                "sg-sub-slot",
                booking       ? "sg-sub-slot--filled"   : isResSub ? "sg-sub-slot--reserved" : "sg-sub-slot--empty",
                isSelected    ? "sg-sub-slot--selected"  : "",
              ].filter(Boolean).join(" ")}
              style={isResSub ? reservedHatchStyle("#f590b0") : { background: color }}
              onClick={() => { if (!isResSub) onSelectSub(sub); }}
            >
              <span className="sg-sub-slot-id">{subLabel}</span>

              {booking ? (
                <>
                  <div className="sg-sub-slot-avatar">
                    {booking.vendorName?.[0]?.toUpperCase() || "?"}
                  </div>
                  <span className="sg-sub-slot-name">{booking.vendorName}</span>
                  <span className={`sg-sub-slot-pill ${booking.selfSelected ? "sg-sub-slot-pill--self" : "sg-sub-slot-pill--admin"}`}>
                    {booking.selfSelected ? <><CrownIcon /> Self</> : <><PencilIcon /> Admin</>}
                  </span>
                  <button
                    className="sg-sub-slot-remove"
                    onClick={e => { e.stopPropagation(); onRemoveSub(sub, booking); }}
                    title="Remove vendor"
                  >✕</button>
                </>
              ) : isResSub ? (
                <>
                  <div className="sg-sub-slot-avatar" style={{ background: "#be123c", color: "#fff" }}>
                    <LockIcon />
                  </div>
                  <span className="sg-sub-slot-name" style={{ color: "#881337", fontWeight: 700 }}>Reserved</span>
                  <button
                    className="sg-sub-slot-remove"
                    onClick={e => { e.stopPropagation(); onUnreserveSub(sub); }}
                    title="Unreserve sub-slot"
                  >✕</button>
                </>
              ) : (
                <>
                  <div className="sg-sub-slot-plus">+</div>
                  <span className="sg-sub-slot-empty-lbl">empty</span>
                </>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ─── Main component ────────────────────────────────────────────────────────────

const SlotGrid = () => {
  const [events, setEvents]                   = useState([]);
  const [selectedEvent, setSelectedEvent]     = useState(null);
  const [selectedEventId, setSelectedEventId] = useState(null);
  const [eventsLoading, setEventsLoading]     = useState(true);

  const [subBookings, setSubBookings]         = useState({});
  const [subReservations, setSubReservations] = useState({});
  const [tentTypes, setTentTypes]             = useState({});
  const [approvedVendors, setApprovedVendors] = useState([]);
  const [reservedSlots, setReservedSlots]     = useState([]);
  const [reservedRowIds, setReservedRowIds]   = useState({});

  const [selectedSlot, setSelectedSlot]       = useState(null);
  const [selectedSub, setSelectedSub]         = useState(null);
  const [isSharing, setIsSharing]             = useState(false);

  const [vendorQuery, setVendorQuery]         = useState("");
  const [message, setMessage]                 = useState("");
  const [messageType, setMessageType]         = useState("info");
  const [toast, setToast]                     = useState(null);
  const [pulsingSlot, setPulsingSlot]         = useState(null);
  const [zoomIndex, setZoomIndex]             = useState(DEFAULT_ZOOM);

  const [mapSearch, setMapSearch]             = useState("");
  const [mapSearchOpen, setMapSearchOpen]     = useState(false);
  const [mapSearchSelected, setMapSearchSelected] = useState(null);

  const gridRef      = useRef(null);
  const slotRefs     = useRef({});
  const mapSearchRef = useRef(null);
  const toastTimer   = useRef(null);
  const vendorInputRef = useRef(null);

  const isLegacy = LEGACY_EVENT_KEY.includes(selectedEvent);

  const fallbackConfig = useMemo(
    () => (isLegacy ? slotsConfigLegacy : slotsConfigSummer),
    [isLegacy]
  );

  const { config: slotsConfig, source: layoutSource } = useEventLayout(selectedEvent, fallbackConfig);

  const activeSlotConfig = useMemo(
    () => slotsConfig.find(s => s.id === selectedSlot),
    [slotsConfig, selectedSlot]
  );

  const effectiveType = useMemo(() => {
    if (!activeSlotConfig) return null;
    if (reservedSlots.includes(selectedSlot)) return "reserved";
    if (activeSlotConfig.type === "20x20") {
      const existing = Object.entries(subBookings)
        .find(([k]) => k.startsWith(selectedSlot + ":"));

      if (existing) {
        if (!existing[1].tent_mode) {
          const subsPresent = new Set(
            Object.keys(subBookings)
              .filter(k => k.startsWith(selectedSlot + ":"))
              .map(k => k.split(":")[1])
          );
          return (subsPresent.size > 1 || subsPresent.has("B")) ? "20x20-double" : "20x20-single";
        }
        return existing[1].tent_mode;
      }
      const existingRes = Object.entries(subReservations)
        .find(([k]) => k.startsWith(selectedSlot + ":"));
      if (existingRes) {
        const subsPresent = new Set(
          Object.keys(subReservations)
            .filter(k => k.startsWith(selectedSlot + ":"))
            .map(k => k.split(":")[1])
        );
        return (subsPresent.size > 1 || subsPresent.has("B")) ? "20x20-double" : "20x20-single";
      }
      return isSharing ? "20x20-double" : "20x20-single";
    }
    return activeSlotConfig.type;
  }, [activeSlotConfig, selectedSlot, subBookings, subReservations, reservedSlots, isSharing]);

  const slotSubBookings = useMemo(() => {
    if (!selectedSlot) return {};
    const out = {};
    Object.entries(subBookings).forEach(([k, v]) => {
      if (k.startsWith(selectedSlot + ":")) {
        const sub = k.split(":")[1];
        out[sub] = v;
      }
    });
    return out;
  }, [selectedSlot, subBookings]);

  const slotSubReservations = useMemo(() => {
    if (!selectedSlot) return {};
    const out = {};
    Object.entries(subReservations).forEach(([k, v]) => {
      if (k.startsWith(selectedSlot + ":")) {
        const sub = k.split(":")[1];
        out[sub] = v;
      }
    });
    return out;
  }, [selectedSlot, subReservations]);

  const tentDef = effectiveType ? TENT_SUBDIVISIONS[effectiveType] : null;
  const isMultiSub = tentDef && tentDef.subs.length > 1;
  const slotFilled = tentDef?.subs.filter(s => slotSubBookings[s]).length ?? 0;
  const slotReserved = tentDef?.subs.filter(s => !slotSubBookings[s] && slotSubReservations[s]).length ?? 0;
  const slotCapacity = tentDef?.subs.length ?? 1;

  const getEffectiveTypeForTile = useCallback((slot) => {
    if (reservedSlots.includes(slot.id)) return "reserved";
    if (slot.type === "20x20") {
      const existing = Object.entries(subBookings).find(([k]) => k.startsWith(slot.id + ":"));
      if (existing && !existing[1].tent_mode) {
        const subsPresent = new Set(
          Object.keys(subBookings)
            .filter(k => k.startsWith(slot.id + ":"))
            .map(k => k.split(":")[1])
        );
        return (subsPresent.size > 1 || subsPresent.has("B")) ? "20x20-double" : "20x20-single";
      }
      if (existing) return existing[1].tent_mode || "20x20";

      const existingRes = Object.entries(subReservations).find(([k]) => k.startsWith(slot.id + ":"));
      if (existingRes) {
        const subsPresent = new Set(
          Object.keys(subReservations)
            .filter(k => k.startsWith(slot.id + ":"))
            .map(k => k.split(":")[1])
        );
        return (subsPresent.size > 1 || subsPresent.has("B")) ? "20x20-double" : "20x20-single";
      }
      return "20x20";
    }
    return slot.type;
  }, [subBookings, subReservations, reservedSlots]);

  const getTileUsed = useCallback((slotId) => {
    const booked   = Object.keys(subBookings).filter(k => k.startsWith(slotId + ":")).length;
    const reserved = Object.keys(subReservations).filter(k => k.startsWith(slotId + ":")).length;
    return booked + reserved;
  }, [subBookings, subReservations]);

  const getTileCap = useCallback((slot) => {
    const et = getEffectiveTypeForTile(slot);
    const def = TENT_SUBDIVISIONS[et];
    return def?.subs.length ?? 1;
  }, [getEffectiveTypeForTile]);

  const mapSearchResults = useMemo(() => {
    if (!mapSearch.trim()) return [];
    const q = mapSearch.toLowerCase();
    const seen = new Map();
    Object.entries(subBookings).forEach(([key, b]) => {
      if (b.vendorName?.toLowerCase().includes(q)) {
        const slotId = key.split(":")[0];
        if (!seen.has(slotId)) seen.set(slotId, []);
        seen.get(slotId).push(b);
      }
    });
    return [...seen.entries()].map(([slotId, vendors]) => {
      const cfg = slotsConfig.find(s => s.id === slotId);
      return { slotId, label: cfg?.label || slotId, vendors };
    });
  }, [mapSearch, subBookings, slotsConfig]);

  const highlightedSlotId = mapSearchSelected || mapSearchResults[0]?.slotId || null;

  const assignedVendorIds = useMemo(
    () => new Set(Object.values(subBookings).map(b => b.vendorId)),
    [subBookings]
  );

  const zoomIn    = useCallback(() => setZoomIndex(i => Math.min(i + 1, ZOOM_LEVELS.length - 1)), []);
  const zoomOut   = useCallback(() => setZoomIndex(i => Math.max(i - 1, 0)), []);
  const zoomReset = useCallback(() => setZoomIndex(DEFAULT_ZOOM), []);

  useEffect(() => {
    const el = gridRef.current;
    if (!el) return;
    const handler = e => {
      if (!e.ctrlKey && !e.metaKey) return;
      e.preventDefault();
      e.deltaY < 0 ? zoomIn() : zoomOut();
    };
    el.addEventListener("wheel", handler, { passive: false });
    return () => el.removeEventListener("wheel", handler);
  }, [zoomIn, zoomOut]);

  const showToast = useCallback((message, undoFn) => {
    if (toastTimer.current) clearTimeout(toastTimer.current);
    setToast({ message, undoFn });
    toastTimer.current = setTimeout(() => setToast(null), 5000);
  }, []);

  const dismissToast = useCallback(() => {
    if (toastTimer.current) clearTimeout(toastTimer.current);
    setToast(null);
  }, []);

  const handleUndo = useCallback(async () => {
    if (toast?.undoFn) await toast.undoFn();
    dismissToast();
  }, [toast, dismissToast]);

  const showMsg = (text, type = "info") => { setMessage(text); setMessageType(type); };

  useEffect(() => {
    if (messageType === "success" && message) {
      const t = setTimeout(() => setMessage(""), 2500);
      return () => clearTimeout(t);
    }
  }, [message, messageType]);

  useEffect(() => {
    if (selectedSlot && !activeSlotConfig && !eventsLoading && slotsConfig.length > 0) {
      setSelectedSlot(null);
      showMsg("This slot's layout changed — please reselect.", "info");
    }
  }, [selectedSlot, activeSlotConfig, eventsLoading, slotsConfig]);

  useEffect(() => {
    const handler = e => {
      if (mapSearchRef.current && !mapSearchRef.current.contains(e.target))
        setMapSearchOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  useEffect(() => {
    setMapSearchSelected(null);
    setMapSearchOpen(mapSearch.trim().length > 0);
  }, [mapSearch]);

  useEffect(() => {
    if (!highlightedSlotId) return;
    const el = slotRefs.current[highlightedSlotId];
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "center", inline: "center" });
      setPulsingSlot(highlightedSlotId);
      const t = setTimeout(() => setPulsingSlot(null), 2000);
      return () => clearTimeout(t);
    }
  }, [highlightedSlotId]);

  useEffect(() => {
    const run = async () => {
      const { data } = await supabase
        .from("events")
        .select("id, name, start_date, event_key")
        .order("start_date", { ascending: false });
      setEvents(data || []);
      const def = data?.find(e => e.name?.toLowerCase().includes("christmas")) || data?.[0];
      if (def) { setSelectedEvent(def.event_key); setSelectedEventId(def.id); }
      setEventsLoading(false);
    };
    run();
  }, []);

  useEffect(() => {
    const run = async () => {
      const { data } = await supabase.from("tent_types").select("type, capacity");
      const map = {};
      data?.forEach(t => (map[t.type] = t));
      setTentTypes(map);
    };
    run();
  }, []);

  useEffect(() => {
    if (!selectedEvent) return;
    const run = async () => {
      if (isLegacy) {
        const { data } = await supabase
          .from("xmas_vendors_2025").select("id, brand_name").eq("status", "approved").order("brand_name");
        setApprovedVendors((data || []).map(v => ({ id: v.id, brand_name: v.brand_name, isLegacy: true })));
      } else {
        if (!selectedEventId) return;
        const { data } = await supabase
          .from("vendor_applications")
          .select("id, vendor_id, vendors(brand_name)")
          .eq("event_id", selectedEventId).eq("status", "approved").order("vendors(brand_name)");
        setApprovedVendors((data || []).map(a => ({
          id: a.id, vendorId: a.vendor_id,
          brand_name: a.vendors?.brand_name || "Unnamed", isLegacy: false,
        })));
      }
    };
    run();
  }, [selectedEvent, selectedEventId, isLegacy]);

  const fetchBookings = useCallback(async () => {
    if (!selectedEvent) return;

    const { data, error } = await supabase
      .from("vendor_slots")
      .select("id, slot_id, sub_id, vendor_id, vendor_application_id, tent_mode, reserved, self_selected")
      .eq("event_key", selectedEvent);

    if (error) { console.error("fetchBookings error:", error); return; }
    if (!data?.length) {
      setSubBookings({}); setSubReservations({});
      setReservedSlots([]); setReservedRowIds({});
      return;
    }

    const appIds    = [...new Set(data.filter(r => r.vendor_application_id).map(r => r.vendor_application_id))];
    const legacyIds = [...new Set(data.filter(r => r.vendor_id && !r.vendor_application_id).map(r => r.vendor_id))];

    let appNames = {};
    if (appIds.length > 0) {
      const { data: apps } = await supabase
        .from("vendor_applications")
        .select("id, vendors(brand_name)")
        .in("id", appIds);
      apps?.forEach(a => {
        appNames[a.id] = a.vendors?.brand_name || "Unknown";
      });
    }

    let legacyNames = {};
    if (legacyIds.length > 0) {
      const { data: lv } = await supabase
        .from("xmas_vendors_2025")
        .select("id, brand_name")
        .in("id", legacyIds);
      lv?.forEach(v => { legacyNames[v.id] = v.brand_name; });
    }

    const map = {};
    const resMap = {};
    const reserved = [];
    const rowIds = {};

    data.forEach(row => {
      if (row.reserved) {
        if (row.sub_id) {
          const key = `${row.slot_id}:${row.sub_id}`;
          resMap[key] = { id: row.id };
        } else {
          reserved.push(row.slot_id);
          rowIds[row.slot_id] = row.id;
        }
        return;
      }

      const sub = row.sub_id || "A";
      const key = `${row.slot_id}:${sub}`;

      if (row.vendor_application_id) {
        map[key] = {
          id:           row.id,
          vendorId:     row.vendor_application_id,
          vendorName:   appNames[row.vendor_application_id] || "Unknown",
          tent_mode:    row.tent_mode,
          selfSelected: row.self_selected,
          isLegacy:     false,
        };
      } else if (row.vendor_id) {
        map[key] = {
          id:           row.id,
          vendorId:     row.vendor_id,
          vendorName:   legacyNames[row.vendor_id] || "Unknown",
          tent_mode:    row.tent_mode,
          selfSelected: row.self_selected,
          isLegacy:     true,
        };
      }
    });

    setSubBookings(map);
    setSubReservations(resMap);
    setReservedSlots(reserved);
    setReservedRowIds(rowIds);
  }, [selectedEvent]);

  useEffect(() => { fetchBookings(); }, [fetchBookings]);

  useEffect(() => {
    setSelectedSub(null);
    setVendorQuery("");
    setMessage("");
    if (!activeSlotConfig) return;
    if (activeSlotConfig.type === "20x20") {
      const existing = Object.entries(subBookings).find(([k]) => k.startsWith(activeSlotConfig.id + ":"));
      setIsSharing(existing ? existing[1].tent_mode === "20x20-double" : false);
    }
  }, [selectedSlot]);

  const assignVendor = async (vendor) => {
    if (!selectedSub || !selectedSlot) return;
    showMsg("");

    const tent_mode = effectiveType === "20x20" ? (isSharing ? "20x20-double" : "20x20-single") : effectiveType;
    const key       = `${selectedSlot}:${selectedSub}`;

    if (subBookings[key]) { showMsg("Sub-slot already filled.", "error"); return; }
    if (subReservations[key]) { showMsg("Sub-slot is reserved — unreserve it first.", "error"); return; }
    if (assignedVendorIds.has(vendor.id)) { showMsg("Vendor already placed elsewhere.", "error"); return; }

    const payload = {
      event_key: selectedEvent,
      slot_id:   selectedSlot,
      sub_id:    selectedSub,
      tent_mode,
      self_selected: false,
    };
    if (isLegacy) payload.vendor_id = vendor.id;
    else          payload.vendor_application_id = vendor.id;

    const { data: inserted, error } = await supabase.from("vendor_slots").insert(payload).select().single();
    if (error) { showMsg(error.message, "error"); return; }

    await fetchBookings();
    setSelectedSub(null);
    setVendorQuery("");
    showMsg(`${vendor.brand_name} placed in ${selectedSlot}${isMultiSub ? selectedSub : ""}.`, "success");

    if (inserted?.id) {
      showToast(`${vendor.brand_name} → ${selectedSlot}${isMultiSub ? selectedSub : ""}`, async () => {
        await supabase.from("vendor_slots").delete().eq("id", inserted.id);
        await fetchBookings();
        showMsg("Assignment undone.", "info");
      });
    }
  };

  const removeVendorFromSub = async (sub, booking) => {
    const snapshot = await supabase.from("vendor_slots").select("*").eq("id", booking.id).single();
    const { error } = await supabase.from("vendor_slots").delete().eq("id", booking.id);
    if (error) { showMsg("Remove failed.", "error"); return; }
    await fetchBookings();
    if (selectedSub === sub) setSelectedSub(null);

    if (snapshot.data) {
      showToast(`${booking.vendorName} removed`, async () => {
        const { id, ...rest } = snapshot.data;
        await supabase.from("vendor_slots").insert(rest);
        await fetchBookings();
        showMsg("Removal undone.", "info");
      });
    }
  };

  const toggleReserveSub = async (sub) => {
    if (!selectedSlot) return;
    const key = `${selectedSlot}:${sub}`;
    const existing = subReservations[key];

    try {
      if (existing) {
        const { error: delErr, count } = await supabase
          .from("vendor_slots")
          .delete({ count: "exact" })
          .eq("id", existing.id);

        if (delErr) { showMsg("Unreserve failed: " + delErr.message, "error"); return; }
        if (!count) { showMsg("Unreserve failed: no matching row deleted.", "error"); }
        else        { showMsg(`${selectedSlot}${isMultiSub ? sub : ""} unreserved.`, "success"); }
      } else {
        if (subBookings[key]) { showMsg("Sub-slot already filled.", "error"); return; }

        const tent_mode = effectiveType === "20x20" ? (isSharing ? "20x20-double" : "20x20-single") : effectiveType;
        const { error: insErr } = await supabase.from("vendor_slots").insert({
          event_key: selectedEvent,
          slot_id:   selectedSlot,
          sub_id:    sub,
          tent_mode,
          reserved:  true,
          vendor_application_id: null,
          vendor_id: null,
        });

        if (insErr) { showMsg("Reserve failed: " + insErr.message, "error"); return; }
        showMsg(`${selectedSlot}${isMultiSub ? sub : ""} reserved.`, "success");
        setSelectedSub(null);
        setVendorQuery("");
      }
      await fetchBookings();
    } catch (err) {
      showMsg("Error: " + err.message, "error");
    }
  };

  const toggleReserve = async () => {
    const isReserved = reservedSlots.includes(selectedSlot);
    try {
      if (isReserved) {
        const rowId = reservedRowIds[selectedSlot];
        if (!rowId) {
          const { data: fallbackRow, error: lookupErr } = await supabase
            .from("vendor_slots")
            .select("id")
            .eq("event_key", selectedEvent)
            .eq("slot_id", selectedSlot)
            .eq("reserved", true)
            .maybeSingle();

          if (lookupErr || !fallbackRow) {
            showMsg("Could not find the reserved row to remove.", "error");
            return;
          }

          const { error: delErr, count } = await supabase
            .from("vendor_slots")
            .delete({ count: "exact" })
            .eq("id", fallbackRow.id);

          if (delErr) { showMsg("Unreserve failed: " + delErr.message, "error"); return; }
        } else {
          const { error: delErr, count } = await supabase
            .from("vendor_slots")
            .delete({ count: "exact" })
            .eq("id", rowId);

          if (delErr) { showMsg("Unreserve failed: " + delErr.message, "error"); return; }
        }
        showMsg("Slot unreserved.", "success");
      } else {
        const hasSubs = Object.keys(subBookings).some(k => k.startsWith(selectedSlot + ":"));
        const hasSubReservations = Object.keys(subReservations).some(k => k.startsWith(selectedSlot + ":"));
        if (hasSubs || hasSubReservations) { showMsg("Remove or unreserve individual sub-slots before reserving the whole tile.", "error"); return; }

        const { error: insErr } = await supabase.from("vendor_slots").insert({
          event_key: selectedEvent, slot_id: selectedSlot,
          vendor_application_id: null, vendor_id: null, reserved: true,
        });

        if (insErr) { showMsg("Reserve failed: " + insErr.message, "error"); return; }
        showMsg("Slot reserved.", "success");
      }
      await fetchBookings();
    } catch (err) {
      showMsg("Error: " + err.message, "error");
    }
  };

  const switchMode = async (newMode) => {
    const hasExisting = Object.keys(subBookings).some(k => k.startsWith(selectedSlot + ":"))
      || Object.keys(subReservations).some(k => k.startsWith(selectedSlot + ":"));
    if (hasExisting) { showMsg("Remove or unreserve vendors before changing mode.", "error"); return; }
    setIsSharing(newMode === "20x20-double");
    setSelectedSub(null);
  };

  const filteredVendors = useMemo(() => {
    if (!vendorQuery.trim()) return [];
    const q = vendorQuery.toLowerCase();
    return approvedVendors
      .filter(v => v.brand_name.toLowerCase().includes(q) && !assignedVendorIds.has(v.id))
      .slice(0, 8);
  }, [vendorQuery, approvedVendors, assignedVendorIds]);

  const handleEventChange = (key) => {
    const ev = events.find(e => e.event_key === key);
    setSelectedSlot(null); setVendorQuery(""); setMessage("");
    setApprovedVendors([]); setSubBookings({}); setSubReservations({});
    setReservedSlots([]); setReservedRowIds({});
    setSelectedEvent(ev?.event_key || null);
    setSelectedEventId(ev?.id || null);
  };

  const totalSlots    = slotsConfig.filter(s => s.type !== "blocker" && s.type !== "pathway").length;
  const occupiedSlots = [...new Set(Object.keys(subBookings).map(k => k.split(":")[0]))].length;

  const reservedTileIds = useMemo(() => new Set([
    ...reservedSlots,
    ...Object.keys(subReservations).map(k => k.split(":")[0]),
  ]), [reservedSlots, subReservations]);

  const reservedCount = reservedTileIds.size;
  const placedVendors = Object.keys(subBookings).length;
  const unplacedVendors = approvedVendors.length - new Set(Object.values(subBookings).map(b => b.vendorId)).size;

  const renderBlocker = (slot) => {
    const isEntry = slot.id === "Entry";
    const isExit  = slot.id === "Exit";
    return (
      <div
        key={slot.id}
        className={[
          "sg-slot sg-slot--blocker",
          isEntry ? "sg-slot--entry" : "",
          isExit  ? "sg-slot--exit"  : "",
          !isEntry && !isExit ? "sg-slot--gap" : "",
        ].filter(Boolean).join(" ")}
        style={{ gridColumn: slot.gridColumn, gridRow: slot.gridRow }}
      >
        {isEntry && <><span className="sg-blocker-icon">→</span><span>Entry</span></>}
        {isExit  && <><span className="sg-blocker-icon">←</span><span>Exit</span></>}
        {!isEntry && !isExit && <span className="sg-blocker-icon">↔</span>}
      </div>
    );
  };

  const renderPathway = (slot) => (
    <Pathway key={slot.id} slot={slot} />
  );

  useEffect(() => {
    const handler = e => { if (e.key === "Escape") setSelectedSlot(null); };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, []);

  if (eventsLoading) return (
    <div className="sg-page">
      <div className="sg-loading"><div className="sg-spinner" /><span>Loading…</span></div>
    </div>
  );

  const isReservedSlot = reservedSlots.includes(selectedSlot);

  return (
    <div className="sg-page">
      <div className="sg-container">

        {/* ── Header ── */}
        <div className="sg-page-header">
          <div>
            <h1 className="sg-page-title">Vendor Map</h1>
            <p className="sg-page-subtitle">Assign vendors to slots · click any tent to manage sub-slots</p>
          </div>
          <div className="sg-header-right">
            {events.length > 0 && (
              <select className="sg-event-select" value={selectedEvent || ""} onChange={e => handleEventChange(e.target.value)}>
                {events.map(ev => (
                  <option key={ev.id} value={ev.event_key}>
                    {ev.name}{ev.start_date ? ` · ${new Date(ev.start_date).getFullYear()}` : ""}
                  </option>
                ))}
              </select>
            )}
            {isLegacy && <span className="sg-legacy-badge">Legacy</span>}
          </div>
        </div>

        {/* ── Stats ── */}
        <div className="sg-stats-row">
          {[
            { val: totalSlots,     lbl: "Total Slots" },
            { val: occupiedSlots,  lbl: "Occupied",       accent: "green" },
            { val: totalSlots - occupiedSlots - reservedCount, lbl: "Available" },
            { val: reservedCount,  lbl: "Reserved",       accent: "amber" },
            { val: placedVendors,  lbl: "Vendors Placed", accent: "green" },
            { val: unplacedVendors, lbl: "Unplaced",       accent: "amber" },
          ].map(({ val, lbl, accent }) => (
            <div key={lbl} className="sg-stat">
              <span className={`sg-stat-val${accent ? ` sg-stat-val--${accent}` : ""}`}>{val}</span>
              <span className="sg-stat-lbl">{lbl}</span>
            </div>
          ))}
        </div>

        {/* ── Map search + legend ── */}
        <div className="sg-toolbar">
          <div className="sg-search-outer" ref={mapSearchRef}>
            <div className={`sg-search-inner ${mapSearchOpen && mapSearchResults.length > 0 ? "sg-search-inner--open" : ""}`}>
              <svg className="sg-search-icon" viewBox="0 0 20 20" fill="none">
                <circle cx="8.5" cy="8.5" r="5.5" stroke="currentColor" strokeWidth="1.6"/>
                <path d="M13 13l3.5 3.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/>
              </svg>
              <input
                className="sg-search-input"
                placeholder="Find vendor on map…"
                value={mapSearch}
                onChange={e => setMapSearch(e.target.value)}
                onFocus={() => mapSearch.trim() && setMapSearchOpen(true)}
              />
              {mapSearch && (
                <button className="sg-search-clear" onClick={() => { setMapSearch(""); setMapSearchSelected(null); }}>✕</button>
              )}
            </div>

            {mapSearchOpen && mapSearch.trim() && (
              <div className="sg-search-dropdown">
                {mapSearchResults.length === 0
                  ? <div className="sg-search-dropdown-empty">No matches for "{mapSearch}"</div>
                  : mapSearchResults.map(({ slotId, label, vendors }) => (
                    <div
                      key={slotId}
                      className={`sg-search-dropdown-row ${mapSearchSelected === slotId ? "sg-search-dropdown-row--active" : ""}`}
                      onClick={() => {
                        setMapSearchSelected(slotId);
                        setMapSearchOpen(false);
                        const el = slotRefs.current[slotId];
                        if (el) el.scrollIntoView({ behavior: "smooth", block: "center", inline: "center" });
                        setPulsingSlot(slotId);
                        setTimeout(() => setPulsingSlot(null), 2000);
                      }}
                    >
                      <span className="sg-search-dropdown-slot">{label}</span>
                      <div className="sg-search-dropdown-vendors">
                        {vendors.map((v, i) => <span key={i}>{v.vendorName}</span>)}
                      </div>
                    </div>
                  ))
                }
              </div>
            )}
          </div>

          <div className="sg-legend">
            {Object.entries(TENT_COLORS).map(([type, color]) => (
              <div key={type} className="sg-legend-item">
                <span className="sg-legend-dot" style={{ background: color }} />
                <span>{TENT_LABELS[type] || type}</span>
              </div>
            ))}
          </div>
        </div>

        {/* ── Grid canvas ── */}
        <div className="sg-grid-canvas" ref={gridRef}>
          <ZoomControls zoomIndex={zoomIndex} onZoomIn={zoomIn} onZoomOut={zoomOut} onReset={zoomReset} />
          <div className="sg-zoom-hint">Ctrl + scroll to zoom</div>

          <div className="sg-grid-scroll">
            <div className="sg-grid-transform" style={{ transform: `scale(${ZOOM_LEVELS[zoomIndex]})` }}>
              <div className="sg-grid">
                {slotsConfig.map(slot => {
                  if (slot.type === "pathway") return renderPathway(slot);
                  if (slot.type === "blocker") return renderBlocker(slot);

                  const et          = getEffectiveTypeForTile(slot);
                  const used        = getTileUsed(slot.id);
                  const cap         = getTileCap(slot);
                  const isRes       = reservedSlots.includes(slot.id);
                  const hasSubRes   = Object.keys(subReservations).some(k => k.startsWith(slot.id + ":"));
                  const isFullyRes  = isRes || (used === cap && hasSubRes && used > 0);
                  const fillR       = cap > 0 ? used / cap : 0;
                  const isPod       = et === "17x17-pod";
                  const hasFacing   = slot.facing && !isRes;

                  // Apply pink background if tile is fully or partially reserved
                  const tileBgColor = isFullyRes ? "#f590b0" : (TENT_COLORS[et] || "#d9d9d9");

                  return (
                    <div
                      key={slot.id}
                      ref={el => (slotRefs.current[slot.id] = el)}
                      className={[
                        "sg-slot",
                        used > 0 && !isRes  ? "sg-slot--occupied"    : "",
                        fillR === 1         ? "sg-slot--full"        : "",
                        isFullyRes || hasSubRes ? "sg-slot--reserved" : "",
                        pulsingSlot === slot.id ? "sg-slot--pulsing" : "",
                        highlightedSlotId === slot.id ? "sg-slot--highlighted" : "",
                        isPod               ? "sg-slot--pod"          : "",
                      ].filter(Boolean).join(" ")}
                      style={{
                        gridColumn:      slot.gridColumn,
                        gridRow:         slot.gridRow,
                        backgroundColor: tileBgColor,
                        position:        "relative",
                        ...(isFullyRes ? reservedHatchStyle(tileBgColor) : {}),
                        ...(hasFacing ? getFacingBorders(slot.facing) : {}),
                      }}
                      onClick={() => setSelectedSlot(slot.id)}
                    >
                      <div className="sg-slot-shine" />
                      {used > 0 && !isRes && (
                        <div className="sg-slot-overlay" style={{ opacity: fillR * 0.3 }} />
                      )}

                      <strong className="sg-slot-label">{slot.label}</strong>

                      <TileSubPreview
                        slotId={slot.id}
                        subBookings={subBookings}
                        subReservations={subReservations}
                        effectiveType={et}
                        isReserved={isRes}
                      />

                      {!isPod && used === 1 && !isRes && !hasSubRes && (
                        <span className="sg-slot-initial">
                          {Object.values(subBookings).find((_, i) =>
                            Object.keys(subBookings)[i]?.startsWith(slot.id + ":")
                          )?.vendorName?.[0]?.toUpperCase()}
                        </span>
                      )}

                      {/* Lock icon for non-sub fully reserved tiles */}
                      {isFullyRes && !isPod && (
                        <span className="sg-slot-lock">
                          <LockIcon />
                        </span>
                      )}

                      {cap > 1 && !isRes && (
                        <small className="sg-slot-count">{used}/{cap}</small>
                      )}

                      {hasFacing && <FacingArrow facing={slot.facing} />}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>

        {/* ── Modal ── */}
        {selectedSlot && activeSlotConfig && (
          <div
            className="sg-modal-backdrop"
            onClick={e => { if (e.target === e.currentTarget) setSelectedSlot(null); }}
          >
            <div className="sg-modal" role="dialog" aria-modal="true">

              <div className="sg-modal-header">
                <div>
                  <h3 className="sg-modal-title">{activeSlotConfig.label}</h3>
                  <div className="sg-modal-meta">
                    <span
                      className="sg-tent-badge"
                      style={{ background: (TENT_COLORS[effectiveType] || "#ccc") + "33", color: TENT_COLORS[effectiveType] || "#666" }}
                    >
                      {TENT_LABELS[effectiveType] || effectiveType}
                    </span>
                    {slotCapacity > 1 && (
                      <span className="sg-modal-occupancy">
                        {slotFilled}/{slotCapacity} filled
                        {slotReserved > 0 ? ` · ${slotReserved} reserved` : ""}
                      </span>
                    )}
                    {activeSlotConfig.facing && (
                      <span className="sg-modal-facing-pill">
                        {FACING_LABEL[activeSlotConfig.facing]}
                      </span>
                    )}
                  </div>
                </div>
                <button className="sg-modal-close" onClick={() => setSelectedSlot(null)}>✕</button>
              </div>

              {activeSlotConfig.type === "20x20" && !isReservedSlot &&
               !Object.keys(subBookings).some(k => k.startsWith(selectedSlot + ":")) &&
               !Object.keys(subReservations).some(k => k.startsWith(selectedSlot + ":")) && (
                <div className="sg-mode-toggle">
                  <button
                    className={`sg-mode-btn ${!isSharing ? "sg-mode-btn--active" : ""}`}
                    onClick={() => switchMode("20x20-single")}
                  >
                    <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                      <rect x="2" y="2" width="10" height="10" rx="2" stroke="currentColor" strokeWidth="1.4"/>
                    </svg>
                    Private (1 vendor)
                  </button>
                  <button
                    className={`sg-mode-btn ${isSharing ? "sg-mode-btn--active" : ""}`}
                    onClick={() => switchMode("20x20-double")}
                  >
                    <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                      <rect x="2" y="2" width="4.5" height="10" rx="1.5" stroke="currentColor" strokeWidth="1.4"/>
                      <rect x="7.5" y="2" width="4.5" height="10" rx="1.5" stroke="currentColor" strokeWidth="1.4"/>
                    </svg>
                    Shared (2 vendors)
                  </button>
                </div>
              )}

              {(isReservedSlot || (
                !Object.keys(subBookings).some(k => k.startsWith(selectedSlot + ":")) &&
                !Object.keys(subReservations).some(k => k.startsWith(selectedSlot + ":"))
              )) && (
                <button
                  className={`sg-reserve-btn ${isReservedSlot ? "sg-reserve-btn--unreserve" : ""}`}
                  onClick={toggleReserve}
                >
                  {isReservedSlot ? "Unreserve slot" : "Mark whole slot as reserved"}
                </button>
              )}

              {message && (
                <div className={`sg-message sg-message--${messageType}`}>{message}</div>
              )}

              {!isReservedSlot && (
                <SubSlotFloorPlan
                  slot={activeSlotConfig}
                  effectiveType={effectiveType}
                  subBookings={slotSubBookings}
                  subReservations={slotSubReservations}
                  selectedSub={selectedSub}
                  onSelectSub={sub => {
                    if (slotSubBookings[sub]) return;
                    if (slotSubReservations[sub]) return;
                    setSelectedSub(prev => prev === sub ? null : sub);
                    setVendorQuery("");
                  }}
                  onRemoveSub={removeVendorFromSub}
                  onUnreserveSub={toggleReserveSub}
                  facing={activeSlotConfig.facing}
                />
              )}

              {selectedSub && !isReservedSlot && (
                <div className="sg-assign-panel">
                  <div className="sg-assign-header">
                    <span className="sg-assign-label">
                      Assigning to <strong>{activeSlotConfig.label}{isMultiSub ? selectedSub : ""}</strong>
                    </span>
                    <button className="sg-assign-cancel" onClick={() => { setSelectedSub(null); setVendorQuery(""); }}>
                      Cancel
                    </button>
                  </div>
                  <input
                    ref={vendorInputRef}
                    autoFocus
                    className="sg-vendor-input"
                    placeholder="Search approved vendor…"
                    value={vendorQuery}
                    onChange={e => setVendorQuery(e.target.value)}
                  />
                  {vendorQuery && (
                    <div className="sg-suggestions">
                      {filteredVendors.length === 0
                        ? <div className="sg-suggestion sg-suggestion--empty">No matches</div>
                        : filteredVendors.map(v => (
                          <div key={v.id} className="sg-suggestion" onClick={() => assignVendor(v)}>
                            <div className="sg-sug-avatar">{v.brand_name[0]}</div>
                            <span>{v.brand_name}</span>
                          </div>
                        ))
                      }
                    </div>
                  )}
                  <button
                    className="sg-reserve-btn"
                    style={{ marginTop: 8 }}
                    onClick={() => toggleReserveSub(selectedSub)}
                  >
                    Reserve this sub-slot instead (no vendor)
                  </button>
                </div>
              )}

              <div className="sg-modal-footer">
                <button className="sg-btn sg-btn--ghost" onClick={() => setSelectedSlot(null)}>Close</button>
              </div>
            </div>
          </div>
        )}
      </div>

      <UndoToast toast={toast} onUndo={handleUndo} onDismiss={dismissToast} />
    </div>
  );
};

export default SlotGrid;